import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js';

/* GSAPイージング一覧
  * power1: 単純な加速・減速 (旧 Linear)
  * power2: より強い加速・減速 (旧 Quad)
  * power3: さらに強い加速・減速 (旧 Cubic)
  * power4: 最も強い加速・減速 (旧 Quart)
  * back: 少し戻してから目標位置へ
  * elastic: バネのような弾む動き
  * bounce: ボールが跳ねるような動き
  * sine: 滑らかな加速・減速
  * expo: 急激な加速・減速
  * circ: 円形のような動き
  * 
  * 各イージングに .in / .out / .inOut を付けることで
  * 開始時 / 終了時 / 両方 でイージングを適用できます
  * 例: power2.inOut, back.out, elastic.in
  */

// ScrollTriggerとScrollToPluginを登録
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);


const gsapEase = {
  back: 'back.out(1.7)',
  power1: 'power1.inOut',
};
/**
  * Wave Scroll Effect（2要素間のスクロール連動アニメーション）
  * @param {Object[]} sections - セクションの配列
  * @param {string} sections[].class - セクションのクラス名
  * @param {Object} sections[].initialState - 初期状態
  * @param {Object} sections[].animation - アニメーション設定
  * @param {Object} sections[].triggerOptions - ScrollTriggerオプション
*/
function waveScrollEffect(sections) {
  const timelines = sections.map((section, index) => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: `${section.class}`,
        ...section.triggerOptions,
      },
    });
    gsap.set(`${section.class}`, section.initialState);
    timeline.to(`${section.class}`, {
      ...section.animation,
      duration: section.animation.duration || 1,
    });

    return timeline;
  });
  // タイムラインの連結
  timelines.reduce((prev, current) => prev.add(current));
  return timelines[0]; // 最初のタイムラインを返す（必要に応じて）
}

export const waveScroll = (triggerClass, targetClass) => {
// 連結波状スクロール効果の設定
  waveScrollEffect([
    {
      class: triggerClass,
      initialState: { y: '-500px' },
      animation: { y: '0' },
      triggerOptions: {
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
        // markers: true,
      },
    },
    {
      class: targetClass,
      initialState: { marginTop: '-100px' },
      animation: { marginTop: '-500px' },
      triggerOptions: {
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
        // markers: true,
      },
    },
  ]);
};

export const scrollParallax = (targetClass) => {
  const targets = document.querySelectorAll(targetClass);
  const breakPoint = 768;
  
  // コメント：現在のビューポート幅を取得する関数
  const isMobile = () => window.innerWidth < breakPoint;
  targets.forEach((target, index) => {
    // モバイル時はsizeのみ半分にする
    const getResponsiveSize = (value, defaultValue) => {
      const baseValue = value || defaultValue;
      return isMobile() ? baseValue / 2 : baseValue;
    };
    
    // sizeのみレスポンシブ対応、scaleは固定値
    const sizePx = getResponsiveSize(target.dataset?.size, 25);
    const scale = target.dataset?.scale || 1.15;
    const targetImg = target.querySelector('img');
    
    // ウィンドウリサイズ時にsizeのみ更新
    const updateValues = () => {
      gsap.set(targetImg, {
        y: getResponsiveSize(target.dataset?.size, 25)
      });
    };
    
  
    // コメント：アニメーションの開始・終了位置を調整
    // - start: 要素が画面下端から25%上の位置に来たら開始（画面外から効果を開始）
    // - end: 要素が画面上端から25%下の位置に来たら終了（画面外まで効果を継続）
    const adjustPercent = isMobile() ? '15%' : '25%';

    // 初期設定
    gsap.set(targetImg, {
      scale: scale,
      y: sizePx,
    });
    gsap.to(targetImg, {
      scrollTrigger: {  
        trigger: target,
        // コメント：トリガー位置の調整
        start: 'top bottom+='+adjustPercent,  // 画面下端から25%上の位置で開始
        end: 'bottom top-='+adjustPercent,    // 画面上端から25%下の位置で終了
        scrub: 1.5,  // スクロールの追従をよりスムーズに
        invalidateOnRefresh: true,
      },
      y: -sizePx,
      ease: 'power2.inOut',
      duration: 1.5,
    });
    
    // コメント：リサイズイベントの登録
    window.addEventListener('resize', updateValues);
  });
};


/**
 * 要素が指定位置に到達したらクラスを付与/削除する関数
 * @param {Object} options - 設定オプション
 * @param {string|HTMLElement} options.target - クラスを付与する対象要素（セレクタまたは要素）
 * @param {number} options.threshold - 発火位置（px）
 * @param {string} options.className - 付与するクラス名（デフォルト: 'is-hit'）
 */
export const scrollTopPosition = (options = {}) => {
  const {
    target,
    el = null,
    threshold = 100,  // デフォルト値: 100px
    className = 'is-hit'
  } = options;

  if (!target) return;

  const targetElement = typeof target === 'string' ? document.querySelector(target) : target;

  const element = el ? document.querySelector(el) : targetElement;

  if (!targetElement) return;
  

  // ScrollTriggerの設定
  ScrollTrigger.create({
    trigger: targetElement,  // 監視する要素
    start: `top top+=${threshold}px`,  // 要素の上端がビューポート上端+threshold pxに到達したら
    end: 'bottom top',  // 要素の下端がビューポート上端に到達するまで
    // elementがnullの場合にエラーが発生するため、存在チェックを追加
    onEnter: () => element && element.classList.add(className),
    onLeaveBack: () => element && element.classList.remove(className),
    // markers: true,  // デバッグ用（位置確認に便利）
  });
};


/**
 * スクロールに応じて要素をブラー処理する関数
 * @param {Object} options - 設定オプション
 * @param {string} options.targetClass - ブラー効果を適用する要素のクラス名
 * @param {number} [options.maxBlur=10] - 最大ブラー値（px）
 * @param {number} [options.maxScale=null] - 最大スケール値
 * @param {Function} [options.onUpdate=null] - スクロール時のコールバック関数(target, blurValue, progress)
 */
export const scrollBlur = (options) => {
  const {
    targetClass,
    maxBlur = null,
    maxScale = null,
    onUpdate = null
  } = options;

  const target = document.querySelector(targetClass);
  if (!target) return;

  gsap.to(target, {
    scrollTrigger: {
      trigger: target,
      start: 'top top',
      end: 'bottom center',
      scrub: true,
      onUpdate: (self) => {
        // スクロール進捗に応じてブラー値を計算
        const blurValue = Math.min(self.progress * maxBlur, maxBlur);
        const progress = self.progress; // 0から1の範囲の進捗率
        
        // ブラー効果の適用
        if (maxBlur) {
          target.style.filter = `blur(${blurValue}px)`;
        }
        
        // スケール効果の適用
        if (maxScale) {
          const scaleValue = 1 + (progress * (maxScale - 1));
          target.style.transform = `scale(${scaleValue})`;
        }

        // コールバック関数の実行（進捗率も渡す）
        if (typeof onUpdate === 'function') {
          onUpdate(target, blurValue, progress);
        }
      }
    }
  });
};


/**
 * スクロール位置に応じてCSS変数を動的に変更する関数
 * @param {Object} options - 設定オプション
 * @param {string} options.selector - 監視する要素のセレクター（例: '[data-section-bg][data-text-color]'）
 * @param {string} [options.targetSelector='body'] - CSS変数を適用する要素のセレクター
 * @param {Object} [options.activeClass] - アクティブ時のクラス設定
 * @param {string} [options.activeClass.className='is-active'] - 付与するクラス名
 * @param {string} [options.activeClass.target=null] - クラスを付与する要素のセレクター（nullの場合はtargetSelectorと同じ）
 * @param {Object} [options.trigger] - トリガー位置の設定
 * @param {string} [options.trigger.start='top top'] - 開始位置（例: 'top center'）
 * @param {string} [options.trigger.end='bottom top'] - 終了位置（例: 'bottom center'）
 * @param {boolean} [options.trigger.markers=false] - マーカー表示（デバッグ用）
 * @param {Object} options.cssVars - CSS変数の設定
 * @param {Object} options.cssVars.attributes - data属性とCSS変数のマッピング（例: {'section-bg': '--current-bg'}）
 * @param {Object} options.cssVars.defaults - CSS変数のデフォルト値
 */
export const scrollChangeColor = (options = {}) => {
  const {
    selector,
    targetSelector = 'body',
    activeClass = {
      className: 'is-active',
      target: null
    },
    trigger = {
      start: 'top top',
      end: 'bottom top',
      markers: false
    },
    cssVars = {
      attributes: {},
      defaults: {}
    }
  } = options;

  const sections = document.querySelectorAll(selector);
  const target = targetSelector === 'body' ? document.body : document.querySelector(targetSelector);
  const activeTarget = activeClass.target ? document.querySelector(activeClass.target) : target;

  if (!sections.length || !target) {
    // console.warn(`Required elements not found for scrollChangeColor: ${selector}, ${targetSelector}`);
    return;
  }
  console.log(sections);
  // 初期状態でデフォルト値を設定
  Object.entries(cssVars.defaults).forEach(([key, value]) => {
    target.style.setProperty(key, value);
    // console.log('Setting default:', key, value);
  });

  sections.forEach((section, index) => {
    // セクションのdata属性から値を取得してマッピング
    const values = {};
    Object.entries(cssVars.attributes).forEach(([dataAttr, cssVar]) => {
      // data属性名からプレフィックスを削除（例：header-bg → headerBg）
      const camelCaseAttr = dataAttr.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      const dataValue = section.dataset[camelCaseAttr];
      
      if (dataValue) {
        values[cssVar] = dataValue;
        // console.log(`Mapped data-${dataAttr}="${dataValue}" to ${cssVar}`);
      }
    });

    ScrollTrigger.create({
      trigger: section,
      start: trigger.start,
      end: trigger.end,
      markers: trigger.markers,
      onEnter: () => {
        activeTarget?.classList.add(activeClass.className);
        // console.log('onEnter section:', index);
        
        // CSS変数を設定
        Object.entries(values).forEach(([key, value]) => {
          // console.log(`Setting ${key} = ${value}`);
          target.style.setProperty(key, value);
        });
      },
      onLeave: () => {
        if (index < sections.length - 1) return;
        console.log('onLeave last section');
        activeTarget?.classList.remove(activeClass.className);
        
        // 最後のセクションを抜けた時にデフォルト値に戻す
        Object.entries(cssVars.defaults).forEach(([key, value]) => {
          // console.log(`Resetting ${key} to ${value}`);
          target.style.setProperty(key, value);
        });
      },
      onEnterBack: () => {
        activeTarget?.classList.add(activeClass.className);
        console.log('onEnterBack section:', index);
        
        // CSS変数を設定
        Object.entries(values).forEach(([key, value]) => {
          // console.log(`Setting ${key} = ${value}`);
          target.style.setProperty(key, value);
        });
      },
      onLeaveBack: () => {
        if (index > 0) return;
        console.log('onLeaveBack first section');
        activeTarget?.classList.remove(activeClass.className);
        
        // 最初のセクションより上に戻った時にデフォルト値に戻す
        Object.entries(cssVars.defaults).forEach(([key, value]) => {
          // console.log(`Resetting ${key} to ${value}`);
          target.style.setProperty(key, value);
        });
      }
    });
  });
};

/**
 * キャリアページのナビゲーション制御
 * @description スクロール位置に応じてナビゲーションの活性状態を制御します
 */
export const careerFollowMenu = () => {
  const navigationItems = document.querySelectorAll('.follow-list-links-item a');
  const contentItems = document.querySelectorAll('.follow-list-item');

  if (!navigationItems.length || !contentItems.length) return;

  // 初期状態で最初のアイテムのみをアクティブにする
  document.querySelectorAll('.follow-list-links-item').forEach((item, index) => {
    item.classList.remove('is-active');
    if (index === 0) {
      item.classList.add('is-active');
    }
  });

  // ヘッダーの高さを取得
  const headerHeight = document.querySelector('header')?.offsetHeight || 0;
  const offset = 50; // 追加のオフセット（必要に応じて調整）
  const triggerOffset = headerHeight + offset;

  // コメント：スクロールトリガーの更新をバッチ処理するためのデバウンス処理
  let updateTimeout;
  const debouncedUpdate = (currentId) => {
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(() => {
      updateActiveState(currentId);
    }, 50); // 50ミリ秒のデバウンス時間
  };

  // スクロール位置の監視とアクティブ状態の更新
  contentItems.forEach((content) => {
    ScrollTrigger.create({
      trigger: content,
      start: `top top+=${triggerOffset}px`,
      end: `bottom top+=${triggerOffset}px`,
      // コメント：スクロールの同期を最適化
      fastScrollEnd: true,
      preventOverlaps: true,
      onEnter: () => {
        debouncedUpdate(content.id);
      },
      onEnterBack: () => {
        debouncedUpdate(content.id);
      }
    });
  });

  // アクティブ状態の更新関数
  function updateActiveState(currentId) {
    if (!currentId) return;

    // コメント：DOMの更新を最適化
    requestAnimationFrame(() => {
      navigationItems.forEach(link => {
        const parentItem = link.closest('.follow-list-links-item');
        const href = link.getAttribute('href');
        const targetId = href ? href.substring(1) : null;

        if (targetId === currentId) {
          if (!parentItem.classList.contains('is-active')) {
            parentItem.classList.add('is-active');
          }
        } else {
          if (parentItem.classList.contains('is-active')) {
            parentItem.classList.remove('is-active');
          }
        }
      });
    });
  }
};
